import _ from 'lodash';
import { StateService } from '@uirouter/core';
import { isUtestAppHost } from './utest-app-host';

class UserService {
  $cookies: any;
  $http: ng.IHttpService;
  $q: ng.IQService;
  $state: StateService;
  cachedMePromise: any;
  lastMeFetch: any;
  ConfigService: any;
  $rootScope: any;
  $window: ng.IWindowService;
  constructor($state: StateService, $http: ng.IHttpService, $cookies, $q: ng.IQService, $rootScope, ConfigService, $window: ng.IWindowService) {
    this.$state = $state;
    this.$http = $http;
    this.cachedMePromise = null;
    this.lastMeFetch = null;
    this.$cookies = $cookies;
    this.$q = $q;
    this.$rootScope = $rootScope;
    this.ConfigService = ConfigService;
    this.$window = $window;
  }

  addProfileUrl(user) {
    user.url = this.$state.href('profile.about', { profileId: user.id }, { absolute: true });
    return user;
  }

  generatedData(response) {
    response.data = this.addProfileUrl(response.data);
    return response;
  }

  addResponseUrls(response) {
    response.data = _.map(response.data, _.bind(this.addProfileUrl, this));
    return response;
  }

  me(allowedAge?: number) {
    const allowedAgeMs = allowedAge !== undefined ? allowedAge * 1000 : Infinity;
    const now = Date.now();
    const stale = !this.cachedMePromise || _.isNull(this.lastMeFetch) || ((now - this.lastMeFetch) > allowedAgeMs);
    if (!stale) {
      return this.$q.when(this.cachedMePromise);
    }

    // Mark the fetch now so concurrent callers share this promise instead of starting another one.
    this.lastMeFetch = now;
    const fetchMe = () => this.$http.get('/api/v1/users/me').then(_.bind(this.generatedData, this));

    // FLO-54097/FLO-54052: with a Rails session (CSRF cookie) always refresh. Without one, only the uTest
    // app host (the tester-app host, where every app page sits behind a Kong keycloak route and a gateway
    // token therefore always exists) bootstraps the Rails session with users/me; on the marketing host the
    // cookie gate keeps anonymous visitors from being bounced to /login by the 401 interceptor. The host is
    // decided from /api/v1/config here (cached promise) rather than from a flag set by the run block, because
    // the very first transition after the Keycloak callback resolves `user` before that run block finishes.
    this.cachedMePromise = this.ConfigService.config()
      .then((config) => {
        const csrfCookieName = this.$rootScope.csrfCookieName || config.data.csrf_cookie_name;
        const hasRailsSession = this.$cookies.get(csrfCookieName) !== undefined;
        return (hasRailsSession || isUtestAppHost(config.data, this.$window.location.origin)) ? fetchMe() : null;
      })
      .then((response) => {
        if (response === null) {
          // anonymous visitor: do not cache, so the next call re-checks the cookie
          this.cachedMePromise = null;
          this.lastMeFetch = null;
        }
        return response;
      }, (error) => {
        // a failed config/users-me chain must not stay cached: the next call retries
        this.cachedMePromise = null;
        this.lastMeFetch = null;
        throw error;
      });

    return this.$q.when(this.cachedMePromise);
  }

  isPlatformLoggedIn() {
    return this.$http.get('/api/v1/users/me/is_platform_logged_in');
  }

  chatUuid() {
    return this.$http.get('/api/v1/users/me/chat_uuid');
  }

  followers(userId, parameters) {
    parameters = _.defaults(parameters || {}, { page: 1, per_page: 10 });

    return this.$http.get(`/api/v1/users/${userId}/followers`, { params: parameters });
  }

  following(userId, parameters) {
    parameters = _.defaults(parameters || {}, { page: 1, per_page: 10 });

    return this.$http.get(`/api/v1/users/${userId}/following`, { params: parameters });
  }

  updateMe(profileData) {
    // Send this request as multipart/form-data, since it might include file uploads
    // (avatar, profile banner)
    const formData = new FormData();

    _.forEach(profileData, (value, key) => {
      if (_.isPlainObject(value)) {
        value = JSON.stringify(value);
      }
      formData.append(key, value);
    });

    this.cachedMePromise = null;

    return this.$http.put('/api/v1/users/me', formData, { headers: { 'Content-Type': undefined } });
  }

  setViewedCommunityPage() {
    return this.$http.patch('/api/v1/users/me', { viewed_community_page: true });
  }

  follow(id) {
    return this.$http.post(`/api/v1/users/${id}/follow`, '');
  }

  unfollow(id) {
    return this.$http.delete(`/api/v1/users/${id}/follow`);
  }

  find(id) {
    const boundAddUrl = _.bind(this.generatedData, this);
    return this.$http.get(`/api/v1/users/${id}`).then(boundAddUrl);
  }

  createTester(registrationData) {
    return this.$http.post('/api/v1/users', registrationData);
  }

  confirmEmail(emailCode) {
    return this.$http.get('/api/v1/users/confirm_email', { params: { code: emailCode } });
  }

  updatePassword(newPassword, passwordCode) {
    return this.$http.put('/api/v1/users/update_password', { newPassword, passwordCode });
  }

  signOut() {
    return this.$http.delete('/api/v1/session', { headers: { 'Content-Type': 'application/json' } });
  }

  resendConfirmationEmail(email) {
    return this.$http.post('/api/v1/users/resend_confirmation_email', { email });
  }

  resendExpiredSetPasswordEmail(userCode) {
    return this.$http.put('/api/v1/users/resend_expired_set_password_email', { code: userCode });
  }

  comments(userId, parameters) {
    parameters = _.defaults(parameters || {}, { page: 1, per_page: 10 });

    return this.$http.get(`/api/v1/users/${userId}/comments`, { params: parameters });
  }

  mentions(userId, parameters) {
    parameters = _.defaults(parameters || {}, { page: 1, per_page: 10 });

    return this.$http.get(`/api/v1/users/${userId}/mentions`, { params: parameters });
  }

  topics(userId, parameters) {
    parameters = _.defaults(parameters || {}, { page: 1, per_page: 10 });
    return this.$http.get(`/api/v1/users/${userId}/topics`, { params: parameters });
  }

  likes(userId, parameters) {
    parameters = _.defaults(parameters || {}, { page: 1, per_page: 10 });
    return this.$http.get(`/api/v1/users/${userId}/likes`, { params: parameters });
  }

  projects() {
    return this.$http.get('/api/v1/users/me/projects');
  }

  invitations() {
    return this.$http.get('/api/v1/users/me/invitations');
  }

  getProfileBanner(user) {
    if (!user.profile_banner) {
      return {
        url: '/utest-static/assets/images/profile-cover-bg.jpg',
      };
    }
    return user.profile_banner;
  }

  userRoleTitle(user) {
    if (user.is_employee) {
      return 'uTest Employee';
    }

    if (user.platform_roles.pm) {
      return 'Project Manager';
    }

    if (user.platform_roles.ttl) {
      return 'Test Team Lead';
    }

    return 'Tester';
  }

  unreadMessages() {
    return this.$http.get('/api/v1/users/me/unread_messages').catch(() => {});
  }

  ssoEnabled() {
    return this.$http.get('/api/v1/users/me/sso_enabled');
  }
}

UserService.$inject = ['$state', '$http', '$cookies', '$q', '$rootScope', 'ConfigService', '$window'];

export default UserService;
